import { useEffect, useRef } from 'react'

import type { ComplianceLessonKey } from '../lib/types'
import type { QuestionDef } from '../questions/types'
import { animateReveal } from './complianceAnimations'

/**
 * Fades and lifts in every newly-revealed question, so uncovering more of the
 * form reads as new content rather than a silent layout shift. Resets on
 * section change, so a new section is not mistaken for a wave of reveals.
 *
 * Deliberately does not scroll: a reveal can land while its own question is
 * unfinished, which took the user off controls they were part-way through.
 */
export const useAnimateRevealedQuestions = (
  section: ComplianceLessonKey,
  questions: QuestionDef[]
): void => {
  const prevSection = useRef<ComplianceLessonKey | null>(null)
  const seenKeys = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (prevSection.current !== section) {
      prevSection.current = section
      seenKeys.current = new Set(questions.map((q) => q.key))
      return
    }
    const newlyRevealed = questions.filter((q) => !seenKeys.current.has(q.key))
    seenKeys.current = new Set(questions.map((q) => q.key))
    for (const q of newlyRevealed) {
      const el = document.getElementById(`question-${q.key}`)
      if (el) animateReveal(el)
    }
  }, [section, questions])
}
